import { useCallback } from 'react';
import {
  SessionStorageKeys,
  unSessionStorageValue,
  useSessionStorage,
} from '@/utils/storage';

/**
 * What the candidate declared on the residency gate.
 *
 * `other`  — not a resident of California or Illinois; the interview proceeds.
 * `ca_il`  — a resident of one of those states; video interviews are blocked
 *            there (BIPA / CCPA), so the candidate cannot continue.
 */
export type ResidencyAnswer = 'other' | 'ca_il';

/**
 * The gate is an attestation, not an identity check: it lives in
 * sessionStorage, so it survives reloads and back-navigation in the same tab
 * and is asked again in a new tab, or in the same tab once a different
 * invitation key arrives.
 */
export const useResidency = () => {
  const [rawAnswer, setStoredAnswer] = useSessionStorage<ResidencyAnswer>(
    SessionStorageKeys.RESIDENCY
  );

  // `useSessionStorage`'s cast to `ResidencyAnswer` is a lie until this line:
  // it happily returns whatever JSON was in storage. Any value that isn't one
  // of the two real answers — tampering today, a renamed answer value
  // tomorrow — must count as no answer, not as junk the rest of the gate has
  // to cope with.
  const answer: ResidencyAnswer | null =
    rawAnswer === 'other' || rawAnswer === 'ca_il' ? rawAnswer : null;

  const setAnswer = useCallback(
    (next: ResidencyAnswer) => {
      setStoredAnswer(next);
    },
    [setStoredAnswer]
  );

  return { answer, setAnswer };
};

/**
 * Drops the stored answer. Called when a new invitation key arrives, so a
 * second candidate on a shared phone is asked again rather than inheriting
 * the previous candidate's declaration.
 */
export const clearResidencyAnswer = () => {
  unSessionStorageValue(SessionStorageKeys.RESIDENCY);
};
